import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Check, Minus, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { addDays, format, isBefore, startOfDay } from 'date-fns';
import { AvailabilityCalendar } from '../components/AvailabilityCalendar';
import { useBooking } from '../context/BookingContext';
export const VillaDetails = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    villas,
    addBooking,
    isDateAvailable
  } = useBooking();
  const villa = villas.find(item => item.id === id);
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  if (!villa) {
    return <div className="grid min-h-[calc(100vh-80px)] place-items-center bg-nordvik-ink px-6 text-center text-2xl font-black uppercase tracking-tight text-white/50" data-id="element-299" data-mp="17qj6fletqkvy">Villa not found</div>;
  }
  const handleDateSelect = (date: Date) => {
    if (!checkIn || checkIn && checkOut) {
      setCheckIn(date);
      setCheckOut(null);
    } else if (isBefore(date, checkIn)) {
      setCheckIn(date);
    } else {
      setCheckOut(date);
    }
  };
  const handleBook = (event: React.FormEvent) => {
    event.preventDefault();
    if (!checkIn || !checkOut || !name || !email) return;
    let valid = true;
    let current = startOfDay(checkIn);
    const end = startOfDay(checkOut);
    while (isBefore(current, end)) {
      if (!isDateAvailable(villa.id, current)) {
        valid = false;
        break;
      }
      current = addDays(current, 1);
    }
    if (!valid) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }
    addBooking({
      villaId: villa.id,
      guestName: name,
      guestEmail: email,
      checkIn: checkIn.toISOString(),
      checkOut: checkOut.toISOString(),
      status: 'Confirmed',
      source: 'Direct'
    });
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      navigate('/');
    }, 3000);
  };
  return <main className="bg-nordvik-ink text-white" data-id="element-300" data-mp="2a227yvg7d7hy">
      <section className="relative min-h-[530px] overflow-hidden border-b border-white/10" data-id="element-301" data-mp="2a5g9k1h1lcoc">
        <img src={villa.images[0]} alt={villa.name} className="absolute inset-0 h-full w-full object-cover opacity-65 grayscale-[15%]" data-id="element-302" data-mp="263lbd5uerfes" />
        <div className="absolute inset-0 bg-gradient-to-r from-nordvik-ink via-nordvik-ink/45 to-transparent" data-id="element-303" data-mp="1wb5u6aqxquuy" />
        <div className="absolute inset-0 bg-gradient-to-t from-nordvik-ink via-transparent to-transparent" data-id="element-304" data-mp="1rddswq4ehwe3" />
        <div className="relative mx-auto flex min-h-[530px] max-w-[1440px] flex-col justify-between px-6 py-8 sm:px-8 lg:px-12 lg:py-12" data-id="element-305" data-mp="1mcgcahz1haep">
          <button onClick={() => navigate(-1)} className="nv-focus inline-flex w-fit items-center gap-2 rounded-sm text-[10px] uppercase tracking-[0.17em] text-white/65 transition-colors hover:text-white" data-id="element-306" data-mp="1ie0nq9nb7tz6">
            <ArrowLeft className="h-4 w-4" data-id="element-307" data-mp="1xbhg2ecmi7o" /> Back to collection
          </button>
          <div className="max-w-3xl" data-id="element-308" data-mp="6vd1oqnignkq">
            <p className="nv-label text-nordvik-yellow" data-id="element-309" data-mp="btekrbkxsmw7">Private residence / live availability</p>
            <h1 className="mt-5 text-5xl font-black uppercase leading-[0.86] tracking-[-0.055em] text-white sm:text-6xl lg:text-[76px]" data-id="element-310" data-mp="3abk30qige8qc">{villa.name}</h1>
            <div className="mt-8 flex flex-wrap divide-x divide-white/20 border-y border-white/20 text-[10px] uppercase tracking-[0.15em] text-white/65" data-id="element-311" data-mp="3famx5fwwaujj">
              <span className="py-3 pr-5" data-id="element-312" data-mp="3p0onkecq0p1m">Sleeps {villa.sleeps}</span>
              <span className="px-5 py-3" data-id="element-313" data-mp="3p1zlvyned4lc">{villa.bedrooms} bedrooms</span>
              <span className="px-5 py-3" data-id="element-314" data-mp="2rkitx9nmcwt2">{villa.bathrooms} bathrooms</span>
              <span className="px-5 py-3 text-nordvik-orange" data-id="element-315" data-mp="2wikoaqqhwiju">${villa.nightlyRate} nightly</span>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-grid mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 py-16 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12 lg:py-24" data-id="element-316" data-mp="y21ytzm2qeub">
        <article className="lg:col-span-7" data-id="element-317" data-mp="1bu8hbscp8xwx">
          <div className="nv-rail pl-6" data-id="element-318" data-mp="172qfz36rkvbf">
            <p className="nv-label text-nordvik-orange" data-id="element-319" data-mp="171p26qyd5uq0">The residence</p>
            <p className="mt-5 max-w-2xl text-xl leading-relaxed text-white/75 sm:text-2xl" data-id="element-320" data-mp="1lr4sy6xwa05p">{villa.description}</p>
          </div>

          <div className="mt-16 border-t border-white/15 pt-8" data-id="element-321" data-mp="1grru0ntr12mr">
            <div className="flex items-end justify-between gap-4" data-id="element-322" data-mp="1us2py59x17es">
              <h2 className="text-2xl font-black uppercase tracking-tight" data-id="element-323" data-mp="1uq8a35di45hk">Inside the stay</h2>
              <span className="nv-label text-white/35" data-id="element-324" data-mp="1pr55tn5ci3an">Included</span>
            </div>
            <ul className="mt-7 grid grid-cols-1 border-l border-t border-white/10 sm:grid-cols-2" data-id="element-325" data-mp="ejur23cy0wn">
              {villa.amenities.map(amenity => <li key={amenity} className="flex items-center gap-3 border-b border-r border-white/10 px-4 py-4 text-sm text-white/70" data-id="element-326" data-mp="2722357b790wf">
                  <Check className="h-4 w-4 text-nordvik-orange" data-id="element-327" data-mp="2c159cv2x1d5z" /> {amenity}
                </li>)}
            </ul>
          </div>

          <blockquote className="mt-16 border-l-2 border-nordvik-yellow bg-white/[0.03] px-6 py-7" data-id="element-328" data-mp="22blo5irqd77y">
            <p className="max-w-xl text-2xl font-bold leading-snug tracking-tight text-white" data-id="element-329" data-mp="1occ8qlbcmh51">“{villa.testimonial.text}”</p>
            <footer className="mt-5 nv-label text-nordvik-yellow" data-id="element-330" data-mp="1tbfcm2flehbf">{villa.testimonial.author}</footer>
          </blockquote>

          {villa.images.slice(1).length > 0 && <div className="mt-16 grid grid-cols-2 gap-3" data-id="element-331" data-mp="1jcpuc8jsauun">
              {villa.images.slice(1).map((image, index) => <div key={image} className="aspect-square overflow-hidden border border-white/10 bg-white/5" data-id="element-332" data-mp="39p5klk9u1pqc" data-mpi={index}>
                  <img src={image} alt={`${villa.name} scene ${index + 2}`} className="h-full w-full object-cover opacity-80 transition-opacity duration-300 hover:opacity-100" data-id="element-333" data-mp="3dpz4m2k5mkdy" />
                </div>)}
            </div>}
        </article>

        <aside className="lg:col-span-5" data-id="element-334" data-mp="hhlgud3uaqhx">
          <div className="sticky top-28 space-y-6" data-id="element-335" data-mp="hio84m52quyd">
            <AvailabilityCalendar villaId={villa.id} selectedDate={checkIn} onSelectDate={handleDateSelect} data-id="element-336" data-mp="cis4wp47b7jv" />
            <div className="nv-shell" data-id="element-337" data-mp="199kjfibhkrjj">
              <form onSubmit={handleBook} className="nv-glass rounded-md p-6 sm:p-8" data-id="element-338" data-mp="198j77aiqhz08">
                <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-5" data-id="element-339" data-mp="158ild4tb2lj5">
                  <div data-id="element-340" data-mp="10ffwh9zh409a">
                    <p className="nv-label text-nordvik-orange" data-id="element-341" data-mp="10emne2d1kynz">Hold your dates</p>
                    <h2 className="mt-2 text-2xl font-black uppercase tracking-tight" data-id="element-342" data-mp="vh4mmz96hpr2">Request a stay</h2>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-nordvik-yellow" data-id="element-343" data-mp="1syuzwyj23gip" />
                </div>
                <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-344" data-mp="1o48owwgvzpz5">
                  <InfoValue label="Check-in" value={checkIn ? format(checkIn, 'MMM d, yyyy') : 'Select date'} data-id="element-345" data-mp="1virlo4sva444" />
                  <InfoValue label="Check-out" value={checkOut ? format(checkOut, 'MMM d, yyyy') : 'Select date'} data-id="element-346" data-mp="20h2tyb3ik6k8" />
                </div>
                <div className="mt-6" data-id="element-347" data-mp="29h97fwz7p6je">
                  <label className="nv-label block text-white/45" htmlFor="guest-count" data-id="element-348" data-mp="29iafrtoufexf">Guests</label>
                  <div className="mt-2 flex items-center justify-between border-b border-white/20 pb-2" data-id="element-349" data-mp="2efalu4lp0hoj">
                    <button type="button" onClick={() => setGuests(Math.max(1, guests - 1))} className="nv-focus grid h-7 w-7 place-items-center text-white/60 hover:text-white" aria-label="Reduce guests" data-id="element-350" data-mp="2je41a2kjiirc"><Minus className="h-3 w-3" data-id="element-351" data-mp="2t46tw1nr1up5" /></button>
                    <select id="guest-count" value={guests} onChange={event => setGuests(Number(event.target.value))} className="appearance-none bg-transparent text-sm text-white outline-none [&>option]:bg-nordvik-ink" data-id="element-352" data-mp="2t71hu35z1xnb">
                      {Array.from({
                      length: villa.sleeps
                    }, (_, index) => <option key={index + 1} value={index + 1} data-id="element-353" data-mp="3nf0jlqguntz3">{index + 1} guest{index ? 's' : ''}</option>)}
                    </select>
                    <button type="button" onClick={() => setGuests(Math.min(villa.sleeps, guests + 1))} className="nv-focus grid h-7 w-7 place-items-center text-white/60 hover:text-white" aria-label="Add guests" data-id="element-354" data-mp="19daqjbt1g3k"><Plus className="h-3 w-3" data-id="element-355" data-mp="34qdghdi8heo5" /></button>
                  </div>
                </div>
                <Field id="guest-name" label="Full name" value={name} onChange={setName} placeholder="Jane Doe" data-id="element-356" data-mp="2zragw8l0w0nk" />
                <Field id="guest-email" label="Email" type="email" value={email} onChange={setEmail} placeholder="jane@example.com" data-id="element-357" data-mp="3egouxtzjn69a" />
                <button type="submit" disabled={!checkIn || !checkOut} className="nv-focus mt-8 w-full rounded-none bg-nordvik-orange px-5 py-4 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-nordvik-ink disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/30" data-id="element-358" data-mp="39oor3qgcrt7f">
                  Request booking
                </button>
              </form>
            </div>
          </div>
        </aside>
      </section>

      <AnimatePresence data-id="element-359" data-mp="mlc9mqz1t1uh">
        {showSuccess && <BookingModal title="Stay confirmed" body="Your reservation is confirmed. The itinerary and invoice have been sent to your email." data-id="element-360" data-mp="mmdvlqhlr8qz" />}
        {showError && <BookingModal title="Dates unavailable" body="Those dates are already taken. Choose an alternative arrival or departure date." error data-id="element-361" data-mp="1f8yncs1zpmv2" />}
      </AnimatePresence>
    </main>;
};
function InfoValue({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-362" data-mp="16awe5p83rnwp"><p className="nv-label text-white/45" data-id="element-363" data-mp="16awig1f4ya2i">{label}</p><p className="mt-2 border-b border-white/20 pb-2 text-sm text-white" data-id="element-364" data-mp="1a93tqgqtyj6b">{value}</p></div>;
}
function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text'
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  type?: string;
}) {
  return <div className="mt-6" data-id="element-365" data-mp="1w5fa41jiccd5"><label htmlFor={id} className="nv-label block text-white/45" data-id="element-366" data-mp="1w4xi5h3vokb1">{label}</label><input id={id} type={type} required value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="nv-focus mt-2 w-full border-b border-white/20 bg-transparent pb-2 text-sm text-white outline-none placeholder:text-white/25 focus:border-nordvik-orange" data-id="element-367" data-mp="213qn2l53n2zl" /></div>;
}
function BookingModal({
  title,
  body,
  error = false
}: {
  title: string;
  body: string;
  error?: boolean;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 grid place-items-center bg-nordvik-ink/85 p-5 backdrop-blur-sm" data-id="element-368" data-mp="1idsc9x9x2vs9"><motion.div initial={{
      y: 14,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 14,
      opacity: 0
    }} className="nv-shell w-full max-w-md" data-id="element-369" data-mp="18e1ixyx3emtr"><div className={`nv-glass rounded-md p-9 ${error ? 'border-l-2 border-l-nordvik-orange' : 'border-l-2 border-l-nordvik-yellow'}`} data-id="element-370" data-mp="2ys0l6p1rl6lf"><p className="nv-label text-nordvik-orange" data-id="element-371" data-mp="r5h3krwzxzl1">Booking update</p><h2 className="mt-3 text-3xl font-black uppercase leading-none tracking-tight text-white" data-id="element-372" data-mp="r60nge67iy1p">{title}</h2><p className="mt-5 leading-relaxed text-white/65" data-id="element-373" data-mp="n5qfivyutxk7">{body}</p></div></motion.div></motion.div>;
}